import React, {
  useEffect,
  cloneElement,
  forwardRef,
  useState,
  useCallback,
  useContext
} from "react";
import mapboxgl from "mapbox-gl";
import {
  vectorToMapboxStyle,
  vectorTileToMapboxStyle,
  jetStreamVectorToMapboxStyle,
  // jetStreamVectorToMapboxStyle
} from "./use-func/tranform-style";
import vallarisStyle from "../map-styles/default";
import darkStyle from "../map-styles/dark";
import {
  MapProps,
  MapserviceType,
  vectorTileType,
  vectorType,
  jetstreamVectorType
} from '../interface-type'
import 'mapbox-gl/src/css/mapbox-gl.css'
import { useCallbackLastObjectA } from "..";
import { MainContext } from "../context/main-context";
import { getValFromPath } from "./use-func/tranform-jetstream";
import { jsonParse } from "./use-func/util";

const passUrlParam = (url: string) => {
  const domainSplit = url.split("?");
  if (domainSplit.length > 1) {
    const paramSplit = domainSplit[1].split("&");
    let params = {};
    paramSplit.map((p: any) => {
      const pSplit = p.split("=");
      params[`${pSplit[0].toLowerCase()}`] = pSplit[1];
      return false;
    });
    return { domain: domainSplit[0], param: params };
  } else {
    return { domain: domainSplit[0] };
  }
};

export type Ref = HTMLDivElement | { current: HTMLDivElement } | any

const Map = forwardRef((props: MapProps, ref: Ref) => {
  const [styleLoaded, setStyleLoaded] = useState(false)
  const { hostA, tokenA } = useContext(MainContext)
  const [prevMs, setPrevMs] = useState<MapserviceType[]>([])
  const [prevVt, setPrevVt] = useState<vectorTileType[]>([])
  const [prevVector, setPrevVector] = useState<vectorType[]>([])
  const [prevJv, setPrevJv] = useState<jetstreamVectorType[]>([])

  useEffect(() => { // init map
    if (!ref.current.mapRender) {
      const mapRender = new mapboxgl.Map({
        container: ref.current,
        style: props.mapStyle === "default" ? vallarisStyle : darkStyle,
        center: props.center,
        zoom: props.zoom,
        preserveDrawingBuffer: true
      })
      mapRender.on('load', () => {
        props.onMapLoaded(mapRender)
        ref.current.mapRender = mapRender
        setStyleLoaded(true)
      })
    }
  }, [])

  useEffect(() => { // useEffect handle mapservice
    if (styleLoaded) {
      if (props.mapservice.length === 0) {
        clearMs();
      } else {
        updateMs();
      }
    }
  }, [styleLoaded, props.mapservice, passUrlParam]);

  useEffect(() => { // useEffect handle vectortile
    if (styleLoaded) {
      updateVectorTile()
    }
  }, [styleLoaded, props.vectorTile]);

  useEffect(() => { // useEffect handle vector
    if (styleLoaded) {
      if (props.vector.length === 0) {
        clearVector();
      } else {
        updateVector();
      }
    }
  }, [styleLoaded, props.vector]);

  useEffect(() => { // useEffect handle jetstreamVector
    if (styleLoaded) {
      updateJetStreamVector()
    }
    return () => { };
  }, [styleLoaded, props.jetstreamVector])

  const updateDataWithEvent = useCallback(
    async (message: any) => {
      const payloadJson = jsonParse(message.payloadString)
      if (payloadJson && payloadJson.storage && props.jetstreamVector.find((jV: any) => jV.fields.storage === payloadJson.storage)) {
        const matchJv = props.jetstreamVector.find((jv: any) => jv.fields.storage === payloadJson.storage)
        if (matchJv) {
          const _genGeojson = await useCallbackLastObjectA(
            props.host ? props.host : hostA,
            props.token ? props.token : tokenA,
            matchJv.fields.vo,
            matchJv.fields.storage,
            1)
          if (ref.current) {
            const mapRender = ref.current.mapRender
            const geoj = {
              "type": "FeatureCollection",
              "features": [
                {
                  "type": "Feature",
                  "properties": _genGeojson[0].data,
                  "geometry": {
                    "type": "Point",
                    "coordinates": [
                      getValFromPath(_genGeojson[0], `${matchJv.fields.lngPath}.${matchJv.fields.lngName}`),
                      getValFromPath(_genGeojson[0], `${matchJv.fields.latPath}.${matchJv.fields.latName}`)
                    ]
                  }
                }
              ]
            }
            mapRender.getSource(matchJv.fields.storage).setData(geoj);
          }
        }
      }
    },
    [ref],
  )

  useEffect(() => {
    if (props.event) {
      updateDataWithEvent(props.event.message)
    }
    return () => {

    };
  }, [props.event, updateDataWithEvent])

  // ===== start handle mapservices =====
  const clearMs = () => {
    const map = ref.current.mapRender
    const listMsLayer = map
      .getStyle()
      .layers.filter((layer: any) => layer.id.includes("mapservice"));
    listMsLayer.map((msL: any) => {
      map.removeLayer(msL.id)
      map.removeSource(msL.id)
    });
  };

  const updateMs = () => {
    const map = ref.current.mapRender
    const mapLayers = map.getStyle().layers
    const forAddLayer = prevMs.length === 0 ? [] : props.mapservice.map((newMs) => {
      if (prevMs.find(pms => pms.id === newMs.id)) {
        return null
      } else {
        return newMs
      }
    }).filter(newms => newms);
    const forRemoveLayer = props.mapservice.length === 0 ? [] : prevMs.map((pms) => {
      if (props.mapservice.find(newMs => pms.id === newMs.id)) {
        return null
      } else {
        return pms
      }
    }).filter(pms => pms);
    if (forRemoveLayer.length > 0) {
      forRemoveLayer.map((ms: object | any) => {
        const msId = mapLayers.find((msid: object | any) => msid.id.includes(ms.id))
        map.removeLayer(msId.id)
        map.removeSource(msId.id)
      })
    }
    if (forAddLayer.length > 0) {
      forAddLayer.map((ms: object | any) => {
        const tranformMs = passUrlParam(ms.url);
        if (tranformMs.param) {
          map.addLayer({
            id: `mapservice-${ms.id}`,
            type: "raster",
            source: {
              type: "raster",
              tiles: [
                `${tranformMs.domain}?bbox={bbox-epsg-3857}&format=${
                tranformMs.param["format"]}&service=${
                tranformMs.param["service"]}&version=${
                tranformMs.param["version"]}&layers=${
                tranformMs.param["layers"]}&request=GetMap&srs=EPSG:3857&transparent=true&width=256&height=256`
              ],
              tileSize: 256
            },
            paint: { "raster-opacity": ms.opacity ? ms.opacity : 1 }
          });
        } else {
          map.addLayer({
            id: `mapservice-${ms.id}`,
            type: "raster",
            source: {
              type: "raster",
              tiles: [`${tranformMs.domain}`],
              tileSize: 256
            },
            paint: { "raster-opacity": ms.opacity ? ms.opacity : 1 }
          });
        }
        return false;
      });
    }
    if (forAddLayer.length === 0 && forRemoveLayer.length === 0) {
      props.mapservice.map((ms: object | any) => {
        const checkHas = map.getStyle().layers.find((layer: object | any) => layer.id.includes(ms.id))
        if (!checkHas) {
          const tranformMs = passUrlParam(ms.url);
          if (tranformMs.param) {
            map.addLayer({
              id: `mapservice-${ms.id}`,
              type: "raster",
              source: {
                type: "raster",
                tiles: [
                  `${tranformMs.domain}?bbox={bbox-epsg-3857}&format=${
                  tranformMs.param["format"]}&service=${
                  tranformMs.param["service"]}&version=${
                  tranformMs.param["version"]}&layers=${
                  tranformMs.param["layers"]}&request=GetMap&srs=EPSG:3857&transparent=true&width=256&height=256`
                ],
                tileSize: 256
              },
              paint: { "raster-opacity": ms.opacity ? ms.opacity : 1 }
            });
          } else {
            map.addLayer({
              id: `mapservice-${ms.id}`,
              type: "raster",
              source: {
                type: "raster",
                tiles: [`${tranformMs.domain}`],
                tileSize: 256
              },
              paint: { "raster-opacity": ms.opacity ? ms.opacity : 1 }
            });
          }
        }
        return false
      })
    }
    setPrevMs(props.mapservice)
  };
  // ===== end handle mapservices =====

  // ===== start handle vectortiles =====
  const updateVectorTile = useCallback(async () => {
    const newVectortileArray = props.vectorTile
    const mapRender = ref.current.mapRender
    const mapLayers = mapRender.getStyle().layers

    const forAddLayer = prevVt.length === 0 ? [] : await newVectortileArray.map((newVt) => {
      if (prevVt.find(pms => pms.id === newVt.id)) {
        return null
      } else {
        return newVt
      }
    }).filter(newvt => newvt);

    const forRemoveLayer = props.vectorTile.length === 0 ? [] : await prevVt.map((vt) => {
      if (props.vectorTile.find(newVt => vt.id === newVt.id)) {
        return null
      } else {
        return vt
      }
    }).filter(vt => vt);

    if (prevVt.length > 0 && props.vectorTile.length === 0) {
      const listVTSource = Object.keys(mapRender.getStyle().sources).filter(
        (source: string) => source.includes("vectorTile")
      );
      const listVTLayer = mapRender
        .getStyle()
        .layers.filter((layer: object | any) => layer.id.includes("vectorTile"));
      listVTLayer.map((vtL: object | any) => mapRender.removeLayer(vtL.id));
      listVTSource.map((vtS: string) => mapRender.removeSource(vtS));
    }
    else if (forAddLayer.length > 0 || forRemoveLayer.length > 0) {
      if (forAddLayer.length > 0) {
        const addLayersArray = await forAddLayer.map(async (vt: object | any) => {
          const resultLayer = await vectorTileToMapboxStyle(vt);
          mapRender.addSource(resultLayer.source.id, resultLayer.source.data);
          return resultLayer.layers
        });
        const addLayersRs = await Promise.all(addLayersArray)
        addLayersRs.map((ls: any) => {
          ls.map((l: any) => mapRender.addLayer(l))
        })
      }
      if (forRemoveLayer.length > 0) {
        const removeLayersArray = await forRemoveLayer.map((vt: object | any) => {
          const listVTLayer = mapLayers.filter((layer: object | any) => layer.id.includes(vt.id));
          return listVTLayer
        })
        removeLayersArray.map(async (groupSource: any) => {
          await groupSource.map(async (l: any, index: number) => {
            await mapRender.removeLayer(l.id)
            if (groupSource.length === index + 1) {
              await mapRender.removeSource(l.source)
            }
          })
        })
      }
    } else if (forAddLayer.length === 0 && forRemoveLayer.length === 0 && newVectortileArray.length > 0) {
      const layersArray = await newVectortileArray.map(async (vt: vectorTileType) => {
        const resultLayer = await vectorTileToMapboxStyle(vt);
        mapRender.addSource(resultLayer.source.id, resultLayer.source.data)
        return resultLayer.layers
      })
      const layersRs = await Promise.all(layersArray)
      layersRs.map((ls: any) => {
        ls.map((l: any) => mapRender.addLayer(l))
      })
    }

    setPrevVt(newVectortileArray)
    return () => {
    };
  }, [ref, props.vectorTile, prevVt])
  // ===== end handle vectortiles =====

  // ===== start handle vectors =====
  const clearVector = () => {
    const map = ref.current.mapRender
    const listVectorSource = Object.keys(map.getStyle().sources).filter(
      (source: string) => source.includes("vector")
    );
    const listVectorLayer = map
      .getStyle()
      .layers.filter((layer: object | any) => layer.id.includes("vector"));
    listVectorLayer.map((vtL: object | any) => map.removeLayer(vtL.id));
    listVectorSource.map((vtS: string) => map.removeSource(vtS));
  }

  const updateVector = () => {
    const map = ref.current.mapRender
    const mapLayers = map.getStyle().layers

    const forAddLayer = prevVector.length === 0 ? [] : props.vector.map((newVector) => {
      if (prevVector.find(prevvector => prevvector.id === newVector.id)) {
        return null
      } else {
        return newVector
      }
    }).filter(newVector => newVector);

    const forRemoveLayer = props.vector.length === 0 ? [] : prevVector.map((vector) => {
      if (props.vector.find(newVector => vector.id === newVector.id)) {
        return null
      } else {
        return vector
      }
    }).filter(vector => vector);

    if (forRemoveLayer.length > 0) {
      forRemoveLayer.map((vector: object | any) => {
        const listVectorLayer = mapLayers.filter((layer: object | any) => layer.id.includes(vector.id));
        listVectorLayer.map((l: object | any, index: number) => {
          map.removeLayer(l.id)
          if (listVectorLayer.length === index + 1) {
            const listVTSource = Object.keys(map.getStyle().sources).filter(
              (source: string) => source.includes(vector.id)
            )
            listVTSource.map((vectorSource: string) => map.removeSource(vectorSource))
          }
        })
      })
    }

    if (forAddLayer.length > 0) {
      forAddLayer.map((vector: object | any) => {
        const resultLayer = vectorToMapboxStyle(vector);
        const layerSource = resultLayer.source;
        const layers = resultLayer.layers;
        map.addSource(layerSource.id, layerSource.data);
        layers.map((layer: any) => {
          map.addLayer(layer);
          return false;
        });
      });
    }

    if (forAddLayer.length === 0 && forRemoveLayer.length === 0) {
      props.vector.map((vector: vectorType) => {
        const resultLayer = vectorToMapboxStyle(vector);
        const layerSource = resultLayer.source;
        const layers = resultLayer.layers;
        const arraySources = Object.keys(map.getStyle().sources);
        if (arraySources.includes(layerSource.id)) {
          map.getSource(layerSource.id).setData(layerSource.data.data);
        } else {
          map.addSource(layerSource.id, layerSource.data);
          layers.map((layer: any) => {
            map.addLayer(layer);
            return false;
          });
        }
      });
    }
    setPrevVector(props.vector)
  }
  // ===== end handle vectors =====

  // ===== start handle jetstreamVector =====
  const updateJetStreamVector = useCallback(async () => {
    const newJvArray = props.jetstreamVector
    const mapRender = ref.current.mapRender
    // const mapLayers = mapRender.getStyle().layers

    if (newJvArray.length > 0 && prevJv.length === 0) {
      const genLayer = await newJvArray.map((jV: any) => {
        const _genGeojson = useCallbackLastObjectA(
          props.host ? props.host : hostA,
          props.token ? props.token : tokenA,
          jV.fields.vo,
          jV.fields.storage,
          1)
        return _genGeojson
      })
      const rs = await Promise.all(genLayer)
      rs.map((d: any, i: number) => {
        const vector = {
          id: newJvArray[i].id,
          data: {
            "type": "FeatureCollection",
            "features": [
              {
                "type": "Feature",
                "properties": d[0].data,
                "geometry": {
                  "type": "Point",
                  "coordinates": [
                    getValFromPath(d[0], `${newJvArray[i].fields.lngPath}.${newJvArray[i].fields.lngName}`),
                    getValFromPath(d[0], `${newJvArray[i].fields.latPath}.${newJvArray[i].fields.latName}`)
                  ]
                }
              }
            ]
          },
          style: null
        }
        const jvMapbox = jetStreamVectorToMapboxStyle(vector)
        const layerSource = jvMapbox.source;
        const layers = jvMapbox.layers;
        mapRender.addSource(layerSource.id, layerSource.data);
        layers.map((layer: any) => {
          mapRender.addLayer(layer);
          return false;
        });
        return jvMapbox
      })
    } else if (newJvArray.length === 0 && prevJv.length > 0) {
      prevJv.map((jV: object | any) => {
        mapRender.removeLayer(jV.id)
        mapRender.removeSource(jV.id)
      });
    }
    setPrevJv(props.jetstreamVector)
  }, [ref, props.jetstreamVector, prevJv])
  // ===== end handle jetstreamVector =====

  return (
    <div ref={ref} className={props.className}>
      {props.children && Array.isArray(props.children)
        ? props.children.map((child: any, index: number) =>
          cloneElement(child, { refMap: ref, key: index })
        )
        : props.children && !Array.isArray(props.children)
          ? cloneElement(props.children, { refMap: ref })
          : null}
    </div>
  );
});

Map.defaultProps = {
  center: [0, 0],
  zoom: 1,
  mapStyle: "default",
  mapservice: [],
  vector: [],
  vectorTile: [],
  jetstreamVector: [],
  event: null,
  host: null,
  token: null,
  onMapLoaded: () => { }
};


export default Map;
